export function getNativePlatform(): 'ios' | 'android' | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const platform = window.navigator.userAgent.includes('ios')
    ? 'ios'
    : window.navigator.userAgent.includes('android')
      ? 'android'
      : undefined;

  return platform === 'ios' || platform === 'android' ? platform : undefined;
}

export function isIOS(): boolean {
  return getNativePlatform() === 'ios';
}

export function isAndroid(): boolean {
  return getNativePlatform() === 'android';
}

export function isWebView(): boolean {
  return (
    typeof window !== 'undefined' && window.ReactNativeWebView !== undefined
  );
}

export function isIosWebview(): boolean {
  return isWebView() && isIOS();
}

export function isAndroidWebView(): boolean {
  return isWebView() && isAndroid();
}

/**
 * Extracts app version from user agent (e.g., "PierreWebView/1.0.13 android" -> "1.0.13")
 */
export function getAppVersionFromUserAgent(userAgent?: string): string | null {
  const source =
    userAgent ??
    (typeof window !== 'undefined' ? window.navigator.userAgent : null);
  if (!source) return null;

  const match = source.match(/PierreWebView\/(\d+\.\d+\.\d+)/);
  return match ? match[1] : null;
}

/**
 * Compares two semver versions. Returns:
 * - negative if v1 < v2
 * - 0 if v1 === v2
 * - positive if v1 > v2
 */
export function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 !== p2) return p1 - p2;
  }
  return 0;
}

/**
 * Checks if the app version is at least the minimum required version
 */
export function isAppVersionAtLeast(
  minVersion: string,
  userAgent?: string,
): boolean {
  const appVersion = getAppVersionFromUserAgent(userAgent);
  if (!appVersion) return false;
  return compareVersions(appVersion, minVersion) >= 0;
}

export function getPlatform(): 'app' | 'web' {
  return isWebView() ? 'app' : 'web';
}

export function getPlatformOS(): 'ios' | 'android' | 'desktop' {
  return getNativePlatform() || 'desktop';
}

/**
 * Determines if RevenueCat should be used for payments on Android.
 * Returns true for Android apps v1.0.15+ (first version with RevenueCat).
 * Returns false for older versions which use Stripe.
 *
 * Old apps: "PierreWebView/1.0 android" → null → false (Stripe)
 * v1.0.14: "PierreWebView/1.0.14 android" → < 1.0.15 → false (Stripe)
 * v1.0.15+: "PierreWebView/1.0.15 android" → >= 1.0.15 → true (RevenueCat)
 */
export function shouldUseRevenueCatForAndroid(): boolean {
  if (!isAndroidWebView()) return false;

  return isAppVersionAtLeast('1.0.15');
}
